// Pure data module (no Angular DI) describing country-specific defaults used across the
// application. This is the single source of truth for country-specific rules — new
// countries are added here, never by scattering `country === 'IN'` checks in components.
//
// Consumed by GlobalSettingsService (src/app/services/global-settings.service.ts), which
// exposes it reactively based on the organization's country (loaded once from
// `Gds/organization-by-base-api` in app.component.ts).

export interface PhoneValidationConfig {
  /** RegExp used for Validators.pattern(...) in reactive forms. */
  pattern: RegExp;
  /** Same rule as a string, for template-driven forms: [pattern]="globalSettings.phoneValidation.patternString" */
  patternString: string;
  placeholder: string;
  maxLength: number;
  /** Dial code shown next to the field, e.g. "+91". Empty when not applicable. */
  dialCode: string;
  errorMessage: string;
}

export interface PostalCodeConfig {
  label: string;
  pattern: RegExp;
  patternString: string;
  placeholder: string;
  maxLength: number;
  errorMessage: string;
}

export interface IdTypeOption {
  label: string;
  value: string;
}

export interface VehicleRegistrationConfig {
  pattern: RegExp;
  patternString: string;
  placeholder: string;
  errorMessage: string;
}

export interface CountryConfig {
  code: string;
  name: string;
  phoneValidation: PhoneValidationConfig;
  postalCode: PostalCodeConfig;
  idTypes: IdTypeOption[];
  vehicleRegistration: VehicleRegistrationConfig;
  /** Label for the state/province/region field. */
  stateLabel: string;
  /** Label for the primary government tax/identity field (PAN in India). */
  taxIdLabel: string;
  dateFormat: string;
  currency: { code: string; symbol: string };
}

export const DEFAULT_COUNTRY_CODE = 'IN';

// ---- India ----
// These patterns are copied verbatim from the existing India-only implementations
// (src/app/public/portal/portal-options.ts and the HRMS document-section ID list) so
// behavior for India-based organizations does not change.
const INDIA_CONFIG: CountryConfig = {
  code: 'IN',
  name: 'India',
  phoneValidation: {
    pattern: /^(?:\+?91|0)?[\s.-]?\d(?:[\s.-]?\d){9}$/,
    patternString: '^(?:\\+?91|0)?[\\s.-]?\\d(?:[\\s.-]?\\d){9}$',
    placeholder: '10-digit mobile number',
    maxLength: 10,
    dialCode: '+91',
    errorMessage: 'Enter a valid 10-digit mobile number',
  },
  postalCode: {
    label: 'PIN Code',
    pattern: /^[1-9][0-9]{5}$/,
    patternString: '^[1-9][0-9]{5}$',
    placeholder: '6-digit PIN code',
    maxLength: 6,
    errorMessage: 'Enter a valid 6-digit PIN code',
  },
  idTypes: [
    { label: 'Aadhaar', value: 'aadhar' },
    { label: 'Pan Card', value: 'pan' },
    { label: 'Passport', value: 'passport' },
    { label: 'Driving License', value: 'driving license' },
    { label: 'Voter Id', value: 'voter id' },
  ],
  // Same format enforced today in transport/fleet-management/add-fleet.
  vehicleRegistration: {
    pattern: /^[A-Z]{2}-?[0-9]{2}-?[A-Z]{1,2}-?[0-9]{1,4}$/,
    patternString: '^[A-Z]{2}-?[0-9]{2}-?[A-Z]{1,2}-?[0-9]{1,4}$',
    placeholder: 'e.g. KL01AB1234',
    errorMessage: 'Enter a valid registration number (e.g. KL01AB1234)',
  },
  stateLabel: 'State',
  taxIdLabel: 'PAN',
  dateFormat: 'dd/MM/yyyy',
  currency: { code: 'INR', symbol: '₹' },
};

// ---- Generic international fallback ----
// Used for any organization whose country code is anything other than 'IN'. Accepts an
// optional leading country code ("+") plus common formatting — spaces, dashes and
// parentheses — around 7-15 actual digits (e.g. "+973 3600 1234", "(973) 3600-1234",
// "97336001234" all pass). Add a dedicated entry to COUNTRY_CONFIGS below for a specific
// country that needs tighter validation instead of special-casing it in a component.
const DEFAULT_CONFIG: CountryConfig = {
  code: 'DEFAULT',
  name: 'International',
  phoneValidation: {
    pattern: /^\+?(?:[\s().-]*\d){7,15}[\s().-]*$/,
    patternString: '^\\+?(?:[\\s().-]*\\d){7,15}[\\s().-]*$',
    placeholder: 'Phone number with country code',
    maxLength: 20,
    dialCode: '',
    errorMessage: 'Enter a valid phone number, including country code',
  },
  postalCode: {
    label: 'Postal Code',
    pattern: /^[A-Za-z0-9][A-Za-z0-9\- ]{2,9}$/,
    patternString: '^[A-Za-z0-9][A-Za-z0-9\\- ]{2,9}$',
    placeholder: 'Postal code',
    maxLength: 10,
    errorMessage: 'Enter a valid postal code',
  },
  idTypes: [
    { label: 'Passport', value: 'passport' },
    { label: 'National ID', value: 'national id' },
    { label: 'Driving Licence', value: 'driving license' },
    { label: 'Residence Permit', value: 'residence permit' },
    { label: 'Other', value: 'other' },
  ],
  // No universal format outside India — any non-empty value is accepted.
  vehicleRegistration: {
    pattern: /^.+$/,
    patternString: '^.+$',
    placeholder: 'Registration number',
    errorMessage: 'Enter a valid registration number',
  },
  stateLabel: 'State / Province / Region',
  taxIdLabel: 'Tax ID',
  dateFormat: 'MM/dd/yyyy',
  currency: { code: 'USD', symbol: '$' },
};

// Add new countries here as they onboard — nothing else in the app should need to change.
export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  IN: INDIA_CONFIG,
  DEFAULT: DEFAULT_CONFIG,
};

export function resolveCountryConfig(countryCode: string | null | undefined): CountryConfig {
  const code = (countryCode || '').trim().toUpperCase();
  return COUNTRY_CONFIGS[code] ?? DEFAULT_CONFIG;
}
